import { useQuery } from "@apollo/client";
import { KeyboardArrowRight } from "@mui/icons-material";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  ListTenantsQuery,
  StaffSearchTollFreeRegistrationsQuery,
  TollFreeSubmissionStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, { TableBox, TableControls } from "~frontend/components/Table";
import { LIST_TENANTS } from "~frontend/graphql/tenant";
import { STAFF_SEARCH_TOLL_FREE_REGISTRATIONS } from "~frontend/graphql/tollFreeRegistrations";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";

import { TOLL_FREE_SORTS } from "../constants";

const submissionStatusDisplayNames = {
  [TollFreeSubmissionStatus.NotSubmitted]: "Not submitted",
  [TollFreeSubmissionStatus.Pending]: "Verifying with Bandwidth",
  [TollFreeSubmissionStatus.Verified]: "Verification complete",
  [TollFreeSubmissionStatus.Failed]: "Verification failed",
} as const;

export const TollFreeRegistrationTable = (): ReactNode => {
  const tenant = useTenant()!;
  const navigate = useNavigate();
  const [sort, setSort] = useQueryParam(
    "tf_sort",
    withDefault(StringParam, TOLL_FREE_SORTS[0].value)
  );
  const [page, setPage] = useQueryParam("tf_page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "tf_page_size",
    withDefault(NumberParam, 25)
  );

  const [tenantFilter, setTenantFilter] = useQueryParam(
    "tf_tenant",
    withDefault(ArrayParam, [] as string[])
  );

  const {
    loading: tenantsLoading,
    error: tenantsError,
    data: tenantsData,
  } = useQuery<ListTenantsQuery>(LIST_TENANTS);

  const searchVariables = {
    input: {
      tenantId: tenant.id,
      limit: pageSize,
      offset: page * pageSize,
      sort,
      tenantIds: tenantFilter,
    },
  };
  const { loading, error, data } =
    useQuery<StaffSearchTollFreeRegistrationsQuery>(
      STAFF_SEARCH_TOLL_FREE_REGISTRATIONS,
      {
        variables: searchVariables,
      }
    );

  function getTenantSlug(tenantId: string): string {
    return tenantsData!.listTenants.find(t => t.id === tenantId)?.slug || "--";
  }

  return (
    <FlexBox direction="column">
      <PageHeader>Toll-Free Registrations</PageHeader>
      <AsyncWrapper
        data={data}
        loading={[loading, tenantsLoading]}
        errors={[error, tenantsError]}
      >
        {({ staffSearchTollFreeRegistrations }) => {
          const pageRows = staffSearchTollFreeRegistrations.registrations;
          return (
            <>
              <TableControls
                filters={[
                  {
                    id: "tenantIds",
                    label: "Tenant",
                    value: tenantFilter as string[],
                    onChange: setTenantFilter,
                    options: (tenantsData?.listTenants || []).map(t => ({
                      value: t.id,
                      label: t.slug,
                    })),
                    multi: true,
                  },
                ]}
                sort={{
                  value: sort,
                  onChange: setSort,
                  options: TOLL_FREE_SORTS.map(sort => ({
                    value: sort.value,
                    label: sort.label,
                  })),
                }}
              />
              <TableBox>
                <Table<
                  StaffSearchTollFreeRegistrationsQuery["staffSearchTollFreeRegistrations"]["registrations"][0]
                >
                  columns={[
                    {
                      key: "tenantId",
                      title: "Tenant",
                      renderValue: reg => getTenantSlug(reg.tenantId),
                    },
                    {
                      key: "subaccountId",
                      title: "Subaccount",
                      renderValue: reg => reg.subaccount.slug,
                    },
                    {
                      key: "organizationName",
                      title: "Org Name",
                      renderValue: reg => reg.organizationName,
                      align: "left",
                    },
                    {
                      key: "createdBy",
                      title: "Created by",
                      renderValue: reg => reg.createdBy?.email || "unknown",
                      align: "left",
                    },
                    {
                      key: "status",
                      title: "Status",
                      renderValue: reg => {
                        return submissionStatusDisplayNames[
                          reg.submissionStatus
                        ];
                      },
                    },
                  ]}
                  rows={pageRows}
                  rowActions={() => [
                    {
                      id: "edit-toll-free-registration",
                      type: "button",
                      icon: <KeyboardArrowRight />,
                      tooltipLabel: "View/Edit/Copy Registration",
                      onClick: reg =>
                        navigate(
                          `/staff/compliance-registrations/toll-free/${reg.id}`
                        ),
                    },
                  ]}
                  emptyState="No registrations found"
                  totalCount={staffSearchTollFreeRegistrations.totalCount || 0}
                  rowsPerPage={pageSize}
                  onChangeRowsPerPage={newPageSize => setPageSize(newPageSize)}
                  page={page}
                  onChangePage={(page: number) => setPage(page)}
                />
              </TableBox>
            </>
          );
        }}
      </AsyncWrapper>
    </FlexBox>
  );
};
